import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import NetworkVisual from '../components/visuals/NetworkVisual';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: "Introduction",
      content: "Welcome to PR Boost. These terms and conditions outline the rules and regulations for the use of our website and services. By accessing this website and using our services, you accept these terms and conditions in full. If you disagree with any part of these terms and conditions, please do not use our website or services."
    },
    {
      title: "Definitions",
      list: [
        { title: "Company", text: "PR Boost" },
        { title: "Client", text: "Any individual or business that uses the services provided by PR Boost." },
        { title: "Services", text: "All services provided by PR Boost, including but not limited to press release distribution, content creation, SEO optimization, and media outreach." }
      ]
    },
    {
      title: "Use of Services",
      subsections: [
        {
          subtitle: "Eligibility",
          text: "By using our services, you represent and warrant that you are at least 18 years old and legally capable of entering into binding contracts."
        },
        {
          subtitle: "Account Registration",
          text: "To use certain services, you may need to register for an account. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account."
        },
        {
          subtitle: "Service Agreement",
          text: "The services provided by PR Boost are subject to the terms of a specific service agreement, which will be provided to you prior to the commencement of any work."
        }
      ]
    },
    {
      title: "Payment Terms",
      subsections: [
        {
          subtitle: "Pricing",
          text: "All prices for our services are listed on our website. Prices are subject to change without notice, but any changes will not affect orders that have already been accepted."
        },
        {
          subtitle: "Payment",
          text: "Payments must be made in full before the commencement of any services. We accept various forms of payment, which will be detailed at the time of purchase."
        },
        {
          subtitle: "Refunds",
          text: "Refunds will only be issued as outlined in our Refund Policy."
        }
      ]
    },
    {
      title: "Intellectual Property",
      subsections: [
        {
          subtitle: "Ownership",
          text: "All content, materials, and intellectual property created by PR Boost in the course of providing services are the property of PR Boost unless otherwise agreed in writing."
        },
        {
          subtitle: "Client Materials",
          text: "You retain all rights to any materials you provide to us for use in the services. You grant PR Boost a non-exclusive, royalty-free license to use, reproduce, and modify such materials as necessary to provide the services."
        }
      ]
    },
    {
      title: "Confidentiality",
      subsections: [
        {
          subtitle: "Non-Disclosure",
          text: "Both parties agree to keep confidential all information that is designated as confidential or that reasonably should be understood to be confidential in nature."
        },
        {
          subtitle: "Exceptions",
          text: "Confidential information does not include information that is publicly available, already known to the recipient, independently developed by the recipient, or rightfully obtained from third parties."
        }
      ]
    },
    {
      title: "Limitation of Liability",
      subsections: [
        {
          subtitle: "No Warranty",
          text: "PR Boost provides its services \"as is\" and without any warranty or guarantee of any kind, express or implied."
        },
        {
          subtitle: "Liability",
          text: "In no event shall PR Boost be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to the use of our services."
        }
      ]
    },
    {
      title: "Termination",
      subsections: [
        {
          subtitle: "Termination by Client",
          text: "You may terminate your use of our services at any time by providing written notice to PR Boost."
        },
        {
          subtitle: "Termination by PR Boost",
          text: "We reserve the right to terminate or suspend your access to our services at any time, with or without cause or notice."
        }
      ]
    },
    {
      title: "Changes to Terms",
      content: "PR Boost reserves the right to modify these terms and conditions at any time. Any changes will be posted on this page, and your continued use of the website and services constitutes acceptance of the modified terms."
    },
    {
      title: "Governing Law",
      content: "These terms and conditions are governed by and construed in accordance with the laws of the jurisdiction in which PR Boost operates, without regard to its conflict of law principles."
    }
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* Header Section */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold uppercase tracking-wider mb-6 border border-blue-500/20"
            >
              <SafeIcon icon={FiIcons.FiFileText} /> Legal Terms
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-7xl font-extrabold text-white mb-6 tracking-tight"
            >
              Terms of <span className="text-blue-500 text-gradient">Service</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-slate-400 font-medium"
            >
              Last updated: {new Date().toLocaleDateString()}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 relative">
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="glass-card p-8 md:p-16 rounded-[2.5rem] border-white/5 bg-[#12151e]/80 backdrop-blur-xl shadow-2xl">
            <div className="space-y-16">
              {sections.map((section, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="group"
                >
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-4">
                    <span className="text-blue-500/80 text-4xl font-mono group-hover:text-blue-400 transition-colors">
                      {(idx + 1).toString().padStart(2, '0')}
                    </span>
                    {section.title}
                  </h2>
                  
                  {section.content && (
                    <p className="text-slate-400 text-lg leading-relaxed mb-6">
                      {section.content}
                    </p>
                  )}

                  {section.subsections && (
                    <div className="grid gap-8 mt-8">
                      {section.subsections.map((sub, sIdx) => (
                        <div key={sIdx} className="bg-white/5 p-6 rounded-2xl border border-white/5">
                          <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                            {sub.subtitle}
                          </h4>
                          <p className="text-slate-400 leading-relaxed">
                            {sub.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {section.list && (
                    <div className="space-y-6 mt-6">
                      {section.list.map((item, lIdx) => (
                        <div key={lIdx} className="flex gap-4">
                          <div className="mt-1.5 text-blue-500">
                            <SafeIcon icon={FiIcons.FiCheckCircle} />
                          </div>
                          <div>
                            <span className="text-white font-bold">{item.title}: </span>
                            <span className="text-slate-400 leading-relaxed">{item.text}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}

              {/* Contact Us Section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="pt-12 border-t border-white/5"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">Contact Information</h2>
                <div className="bg-blue-500/5 border border-blue-500/20 p-8 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex items-center gap-6">
                    <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                      <SafeIcon icon={FiIcons.FiMail} className="text-3xl" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-xl">PR Boost</h4>
                      <p className="text-blue-400 font-medium">support@prboost.io</p>
                    </div>
                  </div>
                  <div className="text-center md:text-right">
                    <p className="text-slate-400 text-sm max-w-[200px]">
                      If you have any questions about these terms and conditions.
                    </p>
                  </div>
                </div>
              </motion.div>

              <p className="text-center text-slate-500 text-sm italic pt-12">
                By using our website and services, you acknowledge that you have read, understood, and agree to be bound by these terms and conditions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;